import { useState, useEffect, useCallback } from "react"
import { Modal, TitleScreen, TextField } from "components/Core"
import { HiX, HiSearch, HiCheckCircle } from "react-icons/hi"
import { MdInventory } from "react-icons/md"
import cn from "classnames"
import usePreRender from "hooks/usePreRender"
import useSearchMiddlewareOSRM from "hooks/useSearchMiddlewareOSRM"
import useInventoryResources from "hooks/customs/useInventoryResources"
import Swal from "sweetalert2"

interface IInventoryAdjustmentModal {
  mounted: boolean
  onClose: () => void
  sku: string | undefined
  onSuccess?: () => void
}

const InventoryAdjustmentModal = ({ mounted, onClose, sku, onSuccess }: IInventoryAdjustmentModal) => {
  const { _color } = usePreRender()
  const { data: sourcesData, search_osrm_action: searchSources } = useSearchMiddlewareOSRM({ entity: "sources" })
  const { action_search_osrm_action } = useSearchMiddlewareOSRM({
    entity: "item_source",
  })
  const { put_adjust_inventory_action, set_loading_app } = useInventoryResources()

  const [searchTerm, setSearchTerm] = useState("")
  const [selectedSource, setSelectedSource] = useState<any>(null)
  const [newQuantity, setNewQuantity] = useState<number | "">("")
  const [isConfirmed, setIsConfirmed] = useState(false)
  const [isCheckingRelation, setIsCheckingRelation] = useState(false)
  const [activeRecord, setActiveRecord] = useState<any>(null)

  // Initial reset
  useEffect(() => {
    if (mounted) {
      setSearchTerm("")
      setSelectedSource(null)
      setNewQuantity("")
      setIsConfirmed(false)
      setIsCheckingRelation(false)
      setActiveRecord(null)
    }
  }, [mounted])

  const handleSearch = useCallback(() => {
    setSelectedSource(null)
    setIsConfirmed(false)
    setNewQuantity("")
    setActiveRecord(null)
    searchSources({
      from: 0,
      size: 10,
      filters: searchTerm ? { id: searchTerm } : undefined,
    })
  }, [searchTerm, searchSources])

  const handleSourceSelect = async (src: any) => {
    if (selectedSource?.id === src.id) {
      setSelectedSource(null)
      setIsConfirmed(false)
      setIsCheckingRelation(false)
      setActiveRecord(null)
      return
    }

    setIsCheckingRelation(true)
    setIsConfirmed(false)
    setActiveRecord(null)
    setSelectedSource(src)

    try {
      const res: any = await action_search_osrm_action("item_source", {
        from: 0,
        size: 1,
        filters: {
          sku: sku,
          source: src.id,
        },
      })

      const record = res?.data?.message?.records?.[0]
      if (record) {
        setNewQuantity(record.quantity ?? 0)
        setActiveRecord(record)
      } else {
        setNewQuantity(0)
        setActiveRecord(null)
      }
    } catch (error) {
      console.error("Error checking relation:", error)
      setActiveRecord(null)
    } finally {
      setIsCheckingRelation(false)
    }
  }

  const getConfirmationMessage = () => {
    if (isCheckingRelation) return ""

    const msg_new = "Aun no hay inventario asignado para esta tienda, deseas asignarlo."
    const msg_edit = "Ya existe una asignacion de inventario para esta tienda, deseas editarla?"
    const msg_zero =
      "Ya existe una asignacion de inventario para esta tienda, pero no hay unidades disponibles, deseas editarla?"

    if (!activeRecord) return msg_new
    if (Number(activeRecord.quantity) > 0) return msg_edit
    return msg_zero
  }

  const on_submit = async () => {
    if (!selectedSource) {
      return Swal.fire("Error", "Debes seleccionar un source", "error")
    }
    if (newQuantity === "" || isNaN(Number(newQuantity))) {
      return Swal.fire("Error", "Ingresa una cantidad válida", "error")
    }

    try {
      set_loading_app(true)
      const body = {
        op: "set",
        items: [
          {
            sku: sku || "",
            quantity: Number(newQuantity),
          },
        ],
      }

      const res: any = await put_adjust_inventory_action({
        source_id: selectedSource.id,
        body,
      })

      if (res?.data?.code === 200) {
        Swal.fire({
          title: "¡Éxito!",
          text: `Inventario para el SKU #${sku} ajustado correctamente a ${newQuantity}.`,
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
        })
        if (onSuccess) onSuccess()
        onClose()
      } else {
        throw new Error(res?.data?.message || "Error al ajustar inventario")
      }
    } catch (error: any) {
      Swal.fire({
        title: "Error",
        text: error?.response?.data?.message || error.message,
        icon: "error",
      })
    } finally {
      set_loading_app(false)
    }
  }

  return (
    <Modal mounted={mounted} className="w-full max-w-2xl" localIconClose={true}>
      <div className="w-full flex flex-col p-4 sm:p-6 lg:p-10 max-h-[90vh] overflow-y-auto custom-scrollbar">
        {/* Header */}
        <div className="w-full flex justify-between items-start mb-8">
          <div className="flex flex-col">
            <span className="text-[12px] font-bold text-gray-400 uppercase tracking-widest mb-1">Gestión de Stock</span>
            <TitleScreen title="Ajustar Inventario" />
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition-all text-gray-500"
          >
            <HiX className="text-xl" />
          </button>
        </div>

        <div className="w-full flex flex-col gap-6">
          {/* Source Search */}
          <div className="flex flex-col gap-3">
            <label className="text-sm font-bold text-gray-700 uppercase tracking-tight">1. Seleccionar Source</label>
            <div className="flex flex-row items-center gap-2">
              <div className="flex-1 relative">
                <input
                  type="text"
                  className="w-full h-11 pl-4 pr-12 text-[14px] bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 focus:border-[#007AFF] transition-all"
                  placeholder="ID del Source (ej: CD_ENTEL)..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  className={cn(
                    "absolute right-1 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-lg text-white transition-all shadow-sm hover:shadow-md",
                    `bg_${_color}`,
                  )}
                >
                  <HiSearch className="text-lg" />
                </button>
              </div>
            </div>

            {/* Results List */}
            <div className="w-full max-h-48 overflow-y-auto border border-gray-100 rounded-xl bg-gray-50/50 mt-1">
              {sourcesData?.records?.length > 0 ? (
                sourcesData.records.map((src: any) => (
                  <div
                    key={src.id}
                    onClick={() => handleSourceSelect(src)}
                    className={cn(
                      "px-4 py-3 flex items-center justify-between cursor-pointer border-b border-gray-100 last:border-0 transition-colors",
                      selectedSource?.id === src.id ? "bg-[#007AFF]/10" : "hover:bg-white",
                    )}
                  >
                    <div className="flex flex-col">
                      <span className="text-[14px] font-bold text-gray-800">{src.id}</span>
                      <span className="text-[12px] text-gray-500 font-medium">{src.name}</span>
                    </div>
                    {selectedSource?.id === src.id && <HiCheckCircle className="text-[#007AFF] text-xl" />}
                  </div>
                ))
              ) : (
                <div className="p-4 text-center text-gray-400 text-sm italic py-8">
                  {searchTerm && sourcesData
                    ? "No se encontraron sources con ese ID"
                    : "Ingresa un ID y presiona el botón para buscar"}
                </div>
              )}
            </div>
          </div>

          {/* Confirmation / Quantity Flow */}
          {selectedSource && (
            <div className="min-h-[180px] flex flex-col justify-center">
              {isCheckingRelation ? (
                <div className="flex flex-col items-center justify-center py-10 gap-4 animate-fade-in">
                  <div className="w-10 h-10 border-4 border-gray-200 border-t-[#007AFF] rounded-full animate-spin"></div>
                  <span className="text-[14px] font-bold text-gray-400 uppercase tracking-widest">
                    Verificando Relación...
                  </span>
                </div>
              ) : (
                <div className="flex flex-col gap-6 animate-fade-in-up">
                  {!isConfirmed ? (
                    <div className="bg-amber-50 border border-amber-200 p-5 rounded-2xl flex flex-col gap-4">
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                          </svg>
                        </div>
                        <div className="flex flex-col">
                          <p className="text-[14px] text-amber-700 font-medium leading-relaxed">
                            {getConfirmationMessage()}
                          </p>
                        </div>
                      </div>
                      <div className="flex justify-end gap-3">
                        <button
                          onClick={() => setSelectedSource(null)}
                          className="px-5 py-2 text-[13px] font-bold text-amber-700 hover:bg-amber-100 rounded-xl transition-all"
                        >
                          Cancelar
                        </button>
                        <button
                          onClick={() => setIsConfirmed(true)}
                          className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-bold rounded-xl shadow-sm transition-all"
                        >
                          Sí, estoy seguro
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3 animate-fade-in-up">
                      <label className="text-sm font-bold text-gray-700 uppercase tracking-tight">
                        2. Nueva Cantidad de Stock
                      </label>
                      <div className="flex flex-col gap-1">
                        <p className="text-[13px] text-gray-500 mb-2">
                          ¿Cuál es la cantidad de stock nueva para este ítem en el source seleccionado?
                        </p>
                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                          <div className="w-full sm:w-32">
                            <TextField
                              label=""
                              type="number"
                              value={newQuantity === "" ? "" : String(newQuantity)}
                              onChange={(e: any) => setNewQuantity(e.target.value)}
                              placeholder="0"
                              min={0}
                            />
                          </div>
                          <div className="w-full sm:flex-1 flex gap-3 justify-end mt-2 sm:mt-0">
                            <button
                              onClick={onClose}
                              className="px-6 py-2.5 rounded-xl text-[14px] font-bold text-gray-500 hover:bg-gray-100 transition-all"
                            >
                              Cancelar
                            </button>
                            <button
                              onClick={on_submit}
                              className={cn(
                                "px-8 py-2.5 rounded-xl text-[14px] font-bold text-white shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2",
                                `bg_${_color}`,
                              )}
                            >
                              <MdInventory className="text-lg" />
                              Ajustar Stock
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  )
}

export default InventoryAdjustmentModal
